import {useCallback, useMemo, useRef} from 'react';
import Analytics from '../utils/Analytics';
import Environment from '../utils/Environment';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';
import {forgetSavedIdentityAfterPurchase} from '../utils/linkOutSession';
import {setActivePaywall} from '../analytics/activePaywall';

/**
 * Legacy per-screen event names to emit ALONGSIDE the standard events during the
 * dashboard-migration window. Each key maps a standard funnel step to the old name that
 * screen used to fire. Omit a key to only fire the standard event for that step. Delete the
 * whole `legacy` block from a screen once its Amplitude charts have been rebuilt on the
 * standard `[Web Payments] Paywall *` names — that's the entire deprecation step.
 */
type LegacyEventNames = {
  viewed?: string;
  trialSelection?: string;
  purchaseSuccess?: string;
};

type UsePaywallAnalyticsOptions = {
  /** Which paywall these events belong to. Must be a registered slug. */
  paywall: PaywallSlug;
  /**
   * Properties attached to every funnel event for this screen (e.g. `{email, locale,
   * isStudent}`). Pass current values on every render — the hook snapshots the latest into
   * a ref so the returned callbacks stay stable while still reading fresh values.
   */
  baseProps?: Record<string, unknown>;
  /** Legacy names to dual-fire during migration. See {@link LegacyEventNames}. */
  legacy?: LegacyEventNames;
};

/**
 * Standard paywall funnel instrumentation: View → Trial Selection → Purchase Success.
 *
 * Every call fires the shared `[Web Payments] Paywall *` event with `env`, `paywall`, and
 * `baseProps` merged in, so screens no longer hand-roll `{env: Environment.getVar(...),
 * paywall: PAYWALL}` on each track. When a `legacy` name is configured for a step, the same
 * payload is also emitted under that old name so existing dashboards keep receiving data
 * until they're migrated.
 *
 * Pair with {@link usePaywallDismissTracking} for Dismissed / Refocused events.
 */
export default function usePaywallAnalytics({paywall, baseProps, legacy}: UsePaywallAnalyticsOptions) {
  const paywallRef = useRef(paywall);
  paywallRef.current = paywall;
  setActivePaywall(paywall);
  const basePropsRef = useRef(baseProps);
  basePropsRef.current = baseProps;
  const legacyRef = useRef(legacy);
  legacyRef.current = legacy;

  const fire = useCallback(
    (standardEvent: string, legacyEvent: string | undefined, props?: Record<string, unknown>) => {
      const payload = {
        env: Environment.getVar('REACT_APP_ENV') ?? 'test',
        paywall: paywallRef.current,
        ...basePropsRef.current,
        ...props,
      };
      Analytics.track(standardEvent, payload);
      if (legacyEvent) {
        Analytics.track(legacyEvent, payload);
      }
    },
    [],
  );

  const viewed = useCallback(
    (props?: Record<string, unknown>) => fire(PAYWALL_EVENTS.viewed, legacyRef.current?.viewed, props),
    [fire],
  );
  const trialSelection = useCallback(
    (props?: Record<string, unknown>) => fire(PAYWALL_EVENTS.trialSelection, legacyRef.current?.trialSelection, props),
    [fire],
  );
  const purchaseSuccess = useCallback(
    (props?: Record<string, unknown>) => {
      forgetSavedIdentityAfterPurchase();
      fire(PAYWALL_EVENTS.purchaseSuccess, legacyRef.current?.purchaseSuccess, props);
    },
    [fire],
  );

  // Referentially stable across renders (all three callbacks are stable), so screens can
  // safely list the returned object in a useEffect dependency array without re-firing.
  return useMemo(() => ({viewed, trialSelection, purchaseSuccess}), [viewed, trialSelection, purchaseSuccess]);
}
